/**
 * UnifiedCard - Main Component
 *
 * A unified card component that handles all content types (activity, collection,
 * board, walking_tour, trip, album) with consistent styling and context-aware actions.
 *
 * Features:
 * - Consistent photo carousel with swipe/arrow navigation
 * - Context-aware action buttons (Discover vs Saved vs Board)
 * - Type-specific content sections
 * - Creator info display (Discover context)
 * - Loading and error states
 * - Accessibility support
 *
 * Usage:
 * ```jsx
 * import UnifiedCard from './components/UnifiedCard';
 * import { normalizeCardData, CONTENT_TYPES, CARD_CONTEXTS } from '../utils/normalizeCardData';
 *
 * // Normalize data first
 * const normalizedData = normalizeCardData(rawItem, CONTENT_TYPES.ACTIVITY, CARD_CONTEXTS.DISCOVER);
 *
 * // Render card
 * <UnifiedCard
 *   data={normalizedData}
 *   actions={{
 *     onLike: () => handleLike(item),
 *     onComment: () => handleComment(item),
 *     onSave: () => handleSave(item)
 *   }}
 * />
 * ```
 */

import React, { memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { CONTENT_TYPES, CARD_CONTEXTS } from '../../utils/normalizeCardData';
import { sanitizeUrl } from '../../utils/sanitizeUrl';
import PhotoCarousel from './PhotoCarousel';
import CreatorRow from './CreatorRow';
import ActionRow from './ActionRow';
import CardBadges, { SponsoredBadge, FriendSocialProofBadge, ArchivedBadge } from './CardBadges';
import { useSponsoredImpression } from '../../services/sponsoredImpressions';
import { usePostCardView } from '../../services/postViews';
import Modal from '../shared/Modal';
import Icon from '../shared/Icon';
import { albumCardTitle } from '../../utils/recapFallbackTitle';
import {
  ActivityContent,
  CollectionContent,
  BoardContent,
  WalkingTourContent,
  TripContent,
  AlbumContent,
  EventGroupContent,
  EventContent,
  VisitPhotoSetContent,
  TourismExperienceContent,
  ViatorExperienceContent,
  TiqetsExperienceContent
} from './ContentSections';

/**
 * Get the appropriate content component for the content type
 */
const getContentComponent = (type) => {
  switch (type) {
    case CONTENT_TYPES.ACTIVITY:
      return ActivityContent;
    case CONTENT_TYPES.COLLECTION:
      return CollectionContent;
    case CONTENT_TYPES.BOARD:
      return BoardContent;
    case CONTENT_TYPES.WALKING_TOUR:
      return WalkingTourContent;
    case CONTENT_TYPES.TRIP:
      return TripContent;
    case CONTENT_TYPES.ALBUM:
      return AlbumContent;
    case CONTENT_TYPES.EVENT_GROUP:
      return EventGroupContent;
    case CONTENT_TYPES.ARTIST_EVENT:
    case CONTENT_TYPES.EVENT:
      return EventContent;
    case CONTENT_TYPES.VISIT_PHOTO_SET:
      return VisitPhotoSetContent;
    case CONTENT_TYPES.TOURISM_EXPERIENCE:
      return TourismExperienceContent;
    case CONTENT_TYPES.VIATOR_EXPERIENCE:
      return ViatorExperienceContent;
    case CONTENT_TYPES.TIQETS_EXPERIENCE:
      return TiqetsExperienceContent;
    default:
      return ActivityContent;
  }
};

/**
 * Get avatar gradient based on content type
 */
const getAvatarGradient = (type) => {
  const gradients = {
    [CONTENT_TYPES.ACTIVITY]: 'from-success-dark to-success',
    [CONTENT_TYPES.COLLECTION]: 'from-accent-dark to-warning',
    [CONTENT_TYPES.BOARD]: 'from-content-secondary to-content-primary',
    [CONTENT_TYPES.WALKING_TOUR]: 'from-primary to-primary-dark',
    [CONTENT_TYPES.TRIP]: 'from-info to-primary',
    [CONTENT_TYPES.ALBUM]: 'from-primary-dark to-primary',
    [CONTENT_TYPES.EVENT_GROUP]: 'from-primary-dark to-primary',
    [CONTENT_TYPES.ARTIST_EVENT]: 'from-primary to-primary-light',
    [CONTENT_TYPES.EVENT]: 'from-primary to-primary-light',
    [CONTENT_TYPES.VISIT_PHOTO_SET]: 'from-error to-error-dark',
    [CONTENT_TYPES.TOURISM_EXPERIENCE]: 'from-success to-success-dark',
    [CONTENT_TYPES.VIATOR_EXPERIENCE]: 'from-info to-primary',
    [CONTENT_TYPES.TIQETS_EXPERIENCE]: 'from-warning to-accent'
  };
  return gradients[type] || 'from-primary to-primary-dark';
};

/**
 * Loading skeleton component
 */
const CardSkeleton = () => {
  const { t } = useTranslation('feedCard');
  return (
    <div className="bg-surface rounded-xl border border-border overflow-hidden animate-pulse" role="status" aria-live="polite">
      <span className="sr-only">{t('card.skeleton.loadingSr')}</span>
      <div className="aspect-[4/3] bg-surface-secondary" />
      <div className="p-4">
        <div className="flex items-center space-x-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-surface-secondary" />
          <div className="flex-1">
            <div className="h-4 bg-surface-secondary rounded-lg w-24 mb-1" />
            <div className="h-3 bg-surface-secondary rounded-lg w-16" />
          </div>
        </div>
        <div className="h-5 bg-surface-secondary rounded-lg w-3/4 mb-2" />
        <div className="h-4 bg-surface-secondary rounded-lg w-1/2 mb-3" />
        <div className="h-8 bg-surface-secondary rounded-lg mt-4" />
      </div>
    </div>
  );
};

/**
 * Error state component
 */
const CardError = ({ message, onRetry }) => {
  const { t } = useTranslation('feedCard');
  return (
    <div className="bg-surface rounded-xl border border-error/30 overflow-hidden p-6 text-center">
      <div className="text-warning mb-2"><Icon name="warning" className="w-10 h-10 mx-auto" /></div>
      <p className="text-content-secondary mb-3">{message || t('card.error.fallbackMessage')}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="text-sm text-primary hover:text-primary-dark font-medium min-h-[44px] px-4 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2"
        >
          {t('card.error.retryButton')}
        </button>
      )}
    </div>
  );
};

/**
 * Main UnifiedCard Component
 *
 * Props:
 * @param {Object} data - Normalized card data from normalizeCardData()
 * @param {Object} actions - Action handler callbacks
 *   @param {Function} actions.onLike - Like button handler
 *   @param {Function} actions.onComment - Comment button handler
 *   @param {Function} actions.onSave - Save button handler
 *   @param {Function} actions.onSchedule - Schedule button handler (activities only)
 *   @param {Function} actions.onOrganize - Organize/manage collections handler
 *   @param {Function} actions.onDelete - Permanent delete handler
 *   @param {Function} actions.onArchive - Archive handler (owned collections/boards)
 *   @param {Function} actions.onRestore - Restore handler (archived items)
 *   @param {Function} actions.onShare - Share handler (owner only)
 *   @param {Function} actions.onRemove - Remove from board handler
 * @param {boolean} isLoading - Show loading skeleton
 * @param {string} error - Error message to display
 * @param {Function} onRetry - Retry handler for error state
 * @param {Function} onCardClick - Handler for card click (navigates to detail by default)
 * @param {boolean} showCreator - Override to show/hide creator row
 * @param {boolean} showFollow - One-tap Follow in the creator row (Discover only)
 * @param {string} className - Additional CSS classes
 */
const UnifiedCard = ({
  data,
  actions = {},
  isLoading = false,
  error = null,
  onRetry,
  onCardClick,
  showCreator,
  showFollow = false,
  className = '',
  // Optional { surface, sizes } for narrow surfaces (e.g. Home's 288px card
  // strips) — defaults inside PhotoCarousel suit the full-width feed.
  imageSizing
}) => {
  const { t } = useTranslation('feedCard');
  const navigate = useNavigate();
  const [showSponsoredInfo, setShowSponsoredInfo] = useState(false);
  // Viewport-based impression tracking for sponsored cards (no-op otherwise)
  const impressionRef = useSponsoredImpression(data);
  // Someone else's post on screen counts as seen, wherever the card is
  // (services/postViews: the save rate's denominator). No-op otherwise.
  const postViewRef = usePostCardView(data);
  const rootRef = useCallback((node) => {
    impressionRef.current = node;
    postViewRef(node);
  }, [impressionRef, postViewRef]);

  // Shared destination handler for card click and carousel view-all.
  // The same-path guard mirrors what <Link> does natively (it replaces
  // instead of pushing when the URL is unchanged): a card whose detail page
  // IS the current page — an event card on its own venue's page — must not
  // stack a duplicate history entry per click, or Back stops working.
  // window.location, not useLocation(): BrowserRouter keeps them in sync,
  // it's read at click time (never stale), and a useLocation subscription
  // here would re-render every mounted card on each navigation.
  const goToDetail = useCallback(() => {
    if (!data?.detailUrl) return;
    if (data.externalLink) {
      window.open(sanitizeUrl(data.detailUrl), '_blank', 'noopener,noreferrer');
    } else if (data.detailUrl !== window.location.pathname) {
      navigate(data.detailUrl);
    }
  }, [data, navigate]);

  // Handle card click
  const handleCardClick = useCallback((e) => {
    // Not from something the card portals out (a card's video viewer, V7):
    // React bubbles a portal's events up the component tree, so a tap on the
    // playing video would otherwise navigate away from it.
    if (e.currentTarget && !e.currentTarget.contains(e.target)) return;
    // Don't navigate if clicking on a button or link
    if (e.target.closest('button') || e.target.closest('a')) {
      return;
    }

    if (onCardClick) {
      onCardClick(data);
    } else {
      goToDetail();
    }
  }, [data, onCardClick, goToDetail]);

  // Handle view all for carousel
  const handleViewAll = goToDetail;

  // Loading state
  if (isLoading) {
    return <CardSkeleton />;
  }

  // Error state
  if (error) {
    return <CardError message={error} onRetry={onRetry} />;
  }

  // No data
  if (!data) {
    return null;
  }

  const {
    type,
    context,
    title,
    carousel,
    engagement,
    creator,
    badge,
    typeData,
    detailUrl
  } = data;

  // Determine if we should show creator row
  const shouldShowCreator = showCreator !== undefined
    ? showCreator
    : context === CARD_CONTEXTS.DISCOVER && creator && !creator.isOwn;

  // Get content component
  const ContentComponent = getContentComponent(type);

  // Determine if user is owner
  const isOwner = creator?.isOwn || false;

  return (
    <div
      ref={rootRef}
      // Hairline frame on the Mist page, no shadow or lift (2026-10-01 refresh)
      className={`bg-surface rounded-card border border-border overflow-hidden transition-colors duration-normal can-hover:hover:border-content-muted h-full flex flex-col cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 ${className}`}
      onClick={handleCardClick}
      role="article"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          // A focused link or button inside the card must keep its native
          // Enter activation. preventDefault on the bubbled keydown cancels
          // it, and handleCardClick's closest() guard then swallows the
          // event entirely — mouse worked, keyboard silently did nothing
          // (venue links, photo credits, the uitinvlaanderen.be credit).
          if (e.target.closest('a, button')) return;
          if (!e.currentTarget.contains(e.target)) return; // a portaled viewer's key
          e.preventDefault();
          handleCardClick(e);
        }
      }}
      aria-label={t('card.ariaLabel', {
        label: badge?.label || t('card.itemFallback'),
        title: type === CONTENT_TYPES.ALBUM ? albumCardTitle(title, typeData) : title
      })}
    >
      {/* Photo Carousel with Action Row Overlay */}
      <div className="relative">
        <PhotoCarousel
          carousel={carousel}
          type={type}
          title={title}
          badge={badge}
          onViewAll={handleViewAll}
          surface={imageSizing?.surface}
          sizes={imageSizing?.sizes}
        />

        {/* Overlay badges */}
        <CardBadges
          type={type}
          typeData={typeData}
          position="top-left"
        />

        {/* Archived marker. Mutually exclusive with the sponsored badge in
            practice — archived content is withdrawn from discovery, so it can
            never be a boosted card — but they share the slot, so guard it. */}
        {data.isArchived && !data.isSponsored && (
          <div className="absolute top-2 right-2 z-raised">
            <ArchivedBadge />
          </div>
        )}

        {/* Sponsored badge — EU DSA Article 26 compliant */}
        {data.isSponsored && (
          <div className="absolute top-2 right-2 z-raised">
            <SponsoredBadge
              sponsorName={data.sponsorName}
              onInfoClick={() => setShowSponsoredInfo(true)}
            />
          </div>
        )}

        {/* Action Row - overlaid on bottom of image.
            pointer-events-none on the container, re-enabled on the row: the
            gradient spans ~84px of the photo and sits ABOVE the slides'
            stacking contexts, so without this it swallowed every click in
            that band — including the CC photo-credit link on carousel
            slides, which bubbled to the card handler and navigated. */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 via-black/40 to-transparent pt-8 pb-2 px-2 z-[2] overflow-hidden pointer-events-none">
          <div className="pointer-events-auto">
          <ActionRow
            type={type}
            context={context}
            engagement={engagement}
            isOwner={isOwner}
            detailUrl={detailUrl}
            variant="overlay"
            typeData={typeData}
            ownerName={creator?.matchedVia ? creator.ownerName : undefined}
            actions={{
              onLike: actions.onLike ? () => actions.onLike(data._raw || data) : undefined,
              onComment: actions.onComment ? () => actions.onComment(data._raw || data) : undefined,
              onSave: actions.onSave ? () => actions.onSave(data._raw || data) : undefined,
              onSchedule: actions.onSchedule ? () => actions.onSchedule(data._raw || data) : undefined,
              onOrganize: actions.onOrganize ? () => actions.onOrganize(data._raw || data) : undefined,
              onArchive: actions.onArchive ? () => actions.onArchive(data._raw || data) : undefined,
              onRestore: actions.onRestore ? () => actions.onRestore(data._raw || data) : undefined,
              onDelete: actions.onDelete ? () => actions.onDelete(data._raw || data) : undefined,
              // A value, not a handler — but it has to be listed here like one.
              // Every wrapper that sets a specific label (Delete vs Unsave vs
              // Delete tour) was having it dropped by this whitelist, so an
              // owned collection's destructive action read "Unsave", the
              // generic ActionRow default, instead of "Delete".
              deleteLabel: actions.deleteLabel,
              onShare: actions.onShare ? () => actions.onShare(data._raw || data) : undefined,
              onRemove: actions.onRemove ? () => actions.onRemove(data._raw || data) : undefined,
              onViewSavedActivities: actions.onViewSavedActivities
                ? () => actions.onViewSavedActivities(data._raw || data)
                : undefined,
              onNotInterested: actions.onNotInterested ? () => actions.onNotInterested(data._raw || data) : undefined,
              onReport: actions.onReport ? () => actions.onReport(data._raw || data) : undefined,
              onBlockUser: actions.onBlockUser ? () => actions.onBlockUser(data._raw || data) : undefined
            }}
          />
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-4 flex flex-col flex-grow">
        {/* Creator Row (Discover context) */}
        {shouldShowCreator && (
          <CreatorRow
            creator={creator}
            showFollow={showFollow}
            timeAgo={data.timeAgo}
            badge={badge}
            avatarGradient={getAvatarGradient(type)}
          />
        )}

        {/* Friend social proof — "Liked by [friend] and X others" */}
        {engagement?.socialProof && (
          <FriendSocialProofBadge
            socialProof={engagement.socialProof}
            className="mb-2 -mt-1"
          />
        )}

        {/* Saved-from indicator — shows creator name for items saved from other users */}
        {!shouldShowCreator && !creator?.isOwn && creator?.name && (
          <div className="flex items-center gap-1.5 mb-2 text-xs text-content-secondary">
            <Icon name="userCircle" className="w-3.5 h-3.5 text-content-muted" />
            <span>
              <Trans t={t} i18nKey="card.savedFrom" values={{ name: creator.name }}>
                Saved from <span className="font-medium text-content-primary">{{ name: creator.name }}</span>
              </Trans>
            </span>
          </div>
        )}

        {/* Type-specific content. `actions` is forwarded so content sections
            can expose inline affordances (e.g. AlbumContent's visibility chip
            opens the share modal via actions.onShare). */}
        <ContentComponent data={data} actions={actions} />
      </div>

      {/* Sponsored content disclosure modal — DSA Article 26 */}
      {showSponsoredInfo && (
        <Modal
          isOpen={showSponsoredInfo}
          onClose={() => setShowSponsoredInfo(false)}
          size="sm"
          position="center"
        >
          <Modal.Header onClose={() => setShowSponsoredInfo(false)}>
            {t('card.sponsoredModal.title')}
          </Modal.Header>
          <Modal.Body>
            <div className="space-y-4 text-sm">
              <div>
                <p className="font-medium text-content-primary">{t('card.sponsoredModal.advertiserLabel')}</p>
                <p className="text-content-secondary">{data.sponsorName || t('card.sponsoredModal.advertiserFallback')}</p>
              </div>
              <div>
                <p className="font-medium text-content-primary">{t('card.sponsoredModal.whySeeingLabel')}</p>
                <p className="text-content-secondary">
                  {t('card.sponsoredModal.whySeeingBody')}
                </p>
              </div>
              <div>
                <p className="font-medium text-content-primary">{t('card.sponsoredModal.controlsLabel')}</p>
                <p className="text-content-secondary">
                  {t('card.sponsoredModal.controlsBody')}
                </p>
              </div>
              <div className="pt-2 border-t border-border">
                <p className="text-xs text-content-muted">
                  {t('card.sponsoredModal.disclosureFooter')}
                </p>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

// Export component and sub-components
export default memo(UnifiedCard);
export { PhotoCarousel, CreatorRow, ActionRow, CardBadges };
export {
  ActivityContent,
  CollectionContent,
  BoardContent,
  WalkingTourContent,
  TripContent,
  AlbumContent,
  VisitPhotoSetContent,
  TourismExperienceContent
} from './ContentSections';
